import * as React from 'react';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { ValidationError } from 'rcd-cpp-ui/ui/icon/ValidationError';
import { Warning } from 'rcd-cpp-ui/ui/icon/Warning';
import { Info } from 'rcd-cpp-ui/ui/icon/Info';

export interface PopoverProps {
  content: JSX.Element | string;
  type: 'error' | 'warning' | 'info' | 'blueinfo' | 'dark';
  children?: JSX.Element;
  pointerPosition?:
    | 'top-start'
    | 'top'
    | 'top-end'
    | 'right-start'
    | 'right'
    | 'right-end'
    | 'left-start'
    | 'left'
    | 'left-end'
    | 'bottom-start'
    | 'bottom'
    | 'bottom-end';
  packed?: boolean;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  pointerStyle?: React.CSSProperties;
}

const mergeStyle = (
  s1: React.CSSProperties,
  s2: React.CSSProperties
): React.CSSProperties => {
  return {
    ...(s1 || {}),
    ...(s2 || {})
  };
};

const PopoverTooltip = styled(
  ({
    className,
    ...props
  }: TooltipProps & { type; packed; pointerStyle; popoverStyle }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  )
)(({ type, packed, popoverStyle, pointerStyle }) => ({
  zIndex: 10000,
  [`& .${tooltipClasses.arrow}`]: mergeStyle(
    {
      color:
        type === 'error'
          ? '#fdcfcf'
          : type === 'warning'
          ? '#fff4ba'
          : type === 'info'
          ? '#efefef'
          : type === 'blueinfo'
          ? '#e4f1f9'
          : type === 'dark'
          ? '#000000'
          : '#efefef'
    },
    pointerStyle
  ),
  [`& .${tooltipClasses.tooltip}`]: mergeStyle(
    {
      borderRadius: '0px',
      border: type === 'info' ? '1px solid rgba(0, 0, 0, 0.125)' : '',
      backgroundColor:
        type === 'error'
          ? '#fdcfcf'
          : type === 'warning'
          ? '#fff4ba'
          : type === 'info'
          ? '#efefef'
          : type === 'blueinfo'
          ? '#e4f1f9'
          : type === 'dark'
          ? '#000000'
          : '#efefef',
      color:
        type === 'warning'
          ? '#000000'
          : type === 'blueinfo'
          ? '#464a4c'
          : type === 'dark'
          ? '#ffffff'
          : type === 'info'
          ? '#757575'
          : '#000000',
      textAlign: 'left',
      lineHeight: 1.17,
      padding: '8px 8px 8px 8px',
      zIndex: 5000,
      boxShadow: '1px 5px 12px rgba(0, 0, 0, 0.12)',
      font: '13px arial, sans-serif',
      minWidth: 'fit-content',
      width: packed ? 'auto' : '250px',
      whiteSpace: packed ? 'nowrap' : 'pre-wrap'
    },
    popoverStyle
  )
}));

export default function TooltipPopover({
  children,
  content,
  type,
  pointerPosition,
  packed,
  id,
  className,
  style,
  pointerStyle
}: PopoverProps) {
  const childrenToRender = !!children ? (
    children
  ) : type === 'error' ? (
    <ValidationError />
  ) : type === 'warning' ? (
    <Warning />
  ) : (
    <Info />
  );

  return (
    <PopoverTooltip
      id={id}
      title={content}
      arrow
      placement={pointerPosition || 'top'}
      type={type}
      packed={packed}
      className={`slider-activator ` + className}
      pointerStyle={pointerStyle}
      popoverStyle={style}
    >
      <span>{childrenToRender}</span>
    </PopoverTooltip>
  );
}
